import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Radio, Users, Search, Filter, Heart, Youtube, Instagram, Twitch as TwitchIcon, Sparkles } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import { useToast } from '../hooks/use-toast';
import FollowModal from '../components/FollowModal';
import { getAvatarUrl, getInitials } from '../utils/avatarUtils';
import MarchMadnessLeaderboard from '../components/MarchMadnessLeaderboard';

const API_URL = '';  // Use relative URLs with proxy

// Helper function to format Cash App URL
const formatCashAppUrl = (cashAppValue) => {
  if (!cashAppValue) return null;
  
  // If it&apos;s already a full URL, return as-is
  if (cashAppValue.startsWith('https://cash.app/')) {
    return cashAppValue;
  }
  
  // Remove $ if present and create URL
  const username = cashAppValue.replace('$', '');
  return `https://cash.app/$${username}`;
};

const Reviewers = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [reviewers, setReviewers] = useState([]);
  const [newReviewerIds, setNewReviewerIds] = useState(new Set()); // Track new reviewer IDs
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLive, setFilterLive] = useState(false);
  const [filterNew, setFilterNew] = useState(searchParams.get('filter') === 'new'); // New filter from URL
  const [followingStatus, setFollowingStatus] = useState({});
  const [notifyingReviewer, setNotifyingReviewer] = useState(null);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showFollowModal, setShowFollowModal] = useState(false);
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState('');

  // eslint-disable-next-line no-unused-vars
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('access_token');

  const fetchReviewers = useCallback(async () => {
    try {
      // Fetch all reviewers
      const response = await axios.get(`${API_URL}/api/reviewers/all`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        params: {
          _t: Date.now()
        }
      });
      console.log('Fetched reviewers:', response.data);
      setReviewers(response.data);
      
      // Also fetch new reviewers to mark them
      try {
        const newRes = await axios.get(`${API_URL}/api/reviewers/new?days=14&limit=50`);
        const newIds = new Set((newRes.data.reviewers || []).map(r => r.id));
        setNewReviewerIds(newIds);
      } catch (e) {
        console.warn('Could not fetch new reviewers:', e);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reviewers:', error);
      toast({
        title: "Error loading reviewers",
        description: "Failed to load reviewer data. Please refresh the page.",
        variant: "destructive"
      });
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchReviewers();
    
    // Refresh reviewers data every 30 seconds to show updated avatars
    const interval = setInterval(() => {
      fetchReviewers();
    }, 30000);

    // Listen for avatar updates
    const handleAvatarUpdate = (event) => {
      console.log('Avatar updated event received, refreshing reviewers...', event.detail);
      fetchReviewers();
    };
    window.addEventListener('avatar-updated', handleAvatarUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('avatar-updated', handleAvatarUpdate);
    };
    
  }, [toast, fetchReviewers]);

  const handleFollowClick = (reviewer) => {
    // Open the follow modal (no login required)
    setSelectedReviewer(reviewer);
    setShowFollowModal(true);
  };

  // eslint-disable-next-line no-unused-vars
  const handleFollowSuccess = (_data) => {
    // Update following status
    setFollowingStatus({
      ...followingStatus,
      [selectedReviewer.id]: true
    });

    // Show success message
    toast({
      title: "✅ You&apos;re now following!",
      description: `You'll receive text updates when ${selectedReviewer.name} goes live.`
    });

    // Refresh reviewers to get updated follower count
    fetchReviewers();
  };

  // eslint-disable-next-line no-unused-vars
  const handleUnfollow = async (reviewerId) => {
    try {
      await axios.post(
        `${API_URL}/api/reviewers/${reviewerId}/unfollow`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFollowingStatus({
        ...followingStatus,
        [reviewerId]: false
      });

      toast({
        title: "Unfollowed",
        description: "You've unfollowed this reviewer."
      });

      fetchReviewers();
    } catch (error) {
      console.error('Error unfollowing reviewer:', error);
    }
  };

  const handleNotifyMe = (reviewer) => {
    setSelectedReviewer(reviewer);
    setShowNotifyModal(true);
  };

  const submitNotifyMe = async () => {
    if (!notifyEmail) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive"
      });
      return;
    }

    setNotifyingReviewer(selectedReviewer.id);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/public/notify-me`,
        {
          email: notifyEmail,
          reviewer_id: selectedReviewer.id,
          notification_type: 'session_live'
        }
      );

      toast({
        title: "✅ Subscribed!",
        description: response.data.message
      });

      setShowNotifyModal(false);
      setNotifyEmail('');
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to subscribe to notifications.",
        variant: "destructive"
      });
    } finally {
      setNotifyingReviewer(null);
    }
  };

  // Helper function to join live session with retry logic
  const handleJoinLiveSession = async (reviewer) => {
    const maxRetries = 3;
    const retryDelay = 500; // milliseconds
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Call endpoint to get active session
        const response = await axios.get(
          `${API_URL}/api/sessions/reviewer/${reviewer.id}/active`
        );
        
        // New response format: {active: boolean, session: object|null}
        if (response.status === 200 && response.data) {
          // Check new format first
          if (response.data.active && response.data.session) {
            const sessionId = response.data.session.session_id || response.data.session.id;
            if (sessionId) {
              navigate(`/session/${sessionId}`);
              return;
            }
          }
          // Legacy format fallback
          else if (response.data.session_id) {
            navigate(`/session/${response.data.session_id}`);
            return;
          }
          // No active session (active: false)
          else if (response.data.active === false) {
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              continue;
            }
            // All retries exhausted, show toast
            toast({
              title: "Session Starting Soon",
              description: "This reviewer is starting a session. Try again in a moment.",
              variant: "default"
            });
            return;
          }
        }
      } catch (error) {
        if (error.response?.status === 404) {
          // Legacy 404 handling - retry
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            continue;
          }
          toast({
            title: "Session Starting Soon",
            description: "This reviewer is starting a session. Try again in a moment.",
            variant: "default"
          });
          return;
        } else if (error.response?.status >= 500) {
          // Server error - show soft message, no redirect loops
          toast({
            title: "Temporarily Unavailable",
            description: "Session service is temporarily unavailable. Please try again shortly.",
            variant: "default"
          });
          return;
        } else {
          // Other error (network, etc.)
          toast({
            title: "Connection Error",
            description: "Unable to join session. Please check your connection.",
            variant: "destructive"
          });
          return;
        }
      }
    }
  };


  const filteredReviewers = reviewers.filter(reviewer => {
    const matchesSearch = reviewer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         reviewer.username?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLive = !filterLive || reviewer.isLive;
    const matchesNew = !filterNew || newReviewerIds.has(reviewer.id);
    return matchesSearch && matchesLive && matchesNew;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading reviewers...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* March Madness Leaderboard - Shows at top when challenge is active */}
      <MarchMadnessLeaderboard />
      
      {/* Header */}
      <section className="bg-white border-b border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold font-montserrat mb-4">
            Find Your <span className="text-[#F2D857]">Perfect Reviewer</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Connect with professional music reviewers across all genres. Get real-time feedback on your tracks.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search reviewers by name or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterNew ? 'default' : 'outline'}
                onClick={() => setFilterNew(!filterNew)}
                className={filterNew ? 'bg-green-500 hover:bg-green-600 text-white' : ''}
                data-testid="filter-new-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {filterNew ? 'New Only' : 'New'}
                {newReviewerIds.size > 0 && (
                  <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded text-xs">
                    {newReviewerIds.size}
                  </span>
                )}
              </Button>
              <Button
                variant={filterLive ? 'default' : 'outline'}
                onClick={() => setFilterLive(!filterLive)}
                className={filterLive ? 'bg-red-500 hover:bg-red-600 text-white' : ''}
              >
                <Radio className="h-4 w-4 mr-2" />
                {filterLive ? 'Live Only' : 'Live'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Reviewers Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredReviewers.length === 0 ? (
            <div className="text-center py-20">
              {reviewers.length === 0 ? (
                /* Empty state when NO complete reviewers exist */
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#F2D857]/20 flex items-center justify-center">
                    <Users className="h-10 w-10 text-[#F2D857]" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">No Featured Reviewers Yet</h3>
                  <p className="text-lg text-gray-500 max-w-md mx-auto">
                    New reviewers are onboarding now. Check back soon to discover talented music reviewers!
                  </p>
                </>
              ) : (
                /* Empty state when filters return no results */
                <>
                  <Filter className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-xl text-gray-500">No reviewers found matching your criteria.</p>
                </>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredReviewers.map((reviewer) => {
                // Use SINGLE CANONICAL avatarUrl field with aggressive cache-busting
                const avatarUrl = getAvatarUrl(reviewer);

                return (
                  <Card key={reviewer.id} className="overflow-hidden border-2 border-gray-100 hover:border-[#F2D857] hover:shadow-xl transition-all group" data-testid={`reviewer-card-${reviewer.username}`}>
                    <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={reviewer.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          style={{ 
                            objectPosition: reviewer.card_image_position === 'top' ? 'center top' : 
                                           reviewer.card_image_position === 'bottom' ? 'center bottom' : 'center center'
                          }}
                          loading="eager"
                          onError={(e) => {
                            // Fallback to initial if image fails to load
                            console.warn(`Failed to load avatar for ${reviewer.name}:`, e.target.src);
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                          onLoad={() => {
                            // Successfully loaded - hide fallback
                            const fallback = document.getElementById(`fallback-${reviewer.id}`);
                            if (fallback) fallback.style.display = 'none';
                          }}
                        />
                      ) : null}
                      {/* Fallback initial avatar (always rendered, shown if no image or load fails) */}
                      <div 
                        id={`fallback-${reviewer.id}`}
                        className="w-full h-full flex items-center justify-center bg-[#F2D857] text-black text-6xl font-bold"
                        style={{ display: avatarUrl ? 'none' : 'flex' }}
                      >
                        {getInitials(reviewer.name)}
                      </div>
                      
                      {/* LIVE Badge + Streaming Platforms - Top Left */}
                      {reviewer.is_live && (
                        <div className="absolute top-4 left-4 flex flex-col gap-1.5">
                          <Badge className="bg-red-500 text-white hover:bg-red-500">
                            <Radio className="h-3 w-3 mr-1 animate-pulse" />
                            LIVE
                          </Badge>
                          {/* Streaming Platform Icons */}
                          {reviewer.live_streaming_platforms && reviewer.live_streaming_platforms.length > 0 && (
                            <div className="flex gap-1">
                              {reviewer.live_streaming_platforms.includes('youtube') && (
                                <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center" title="Live on YouTube">
                                  <Youtube className="w-3 h-3 text-white" />
                                </div>
                              )}
                              {reviewer.live_streaming_platforms.includes('tiktok') && (
                                <div className="w-5 h-5 rounded-full bg-black flex items-center justify-center" title="Live on TikTok">
                                  <FaTiktok className="w-2.5 h-2.5 text-white" />
                                </div>
                              )}
                              {reviewer.live_streaming_platforms.includes('twitch') && (
                                <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center" title="Live on Twitch">
                                  <TwitchIcon className="w-3 h-3 text-white" />
                                </div>
                              )}
                              {reviewer.live_streaming_platforms.includes('instagram') && (
                                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center" title="Live on Instagram">
                                  <Instagram className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Verified & PRO Badges - Top Right */}
                      <div className="absolute top-4 right-4 flex flex-col gap-2">
                        {/* NEW Badge - for recently joined reviewers */}
                        {newReviewerIds.has(reviewer.id) && (
                          <Badge className="bg-green-500 text-white hover:bg-green-500" data-testid="new-badge">
                            <Sparkles className="h-3 w-3 mr-1" />
                            NEW
                          </Badge>
                        )}
                        {/* Profile Completion Badge */}
                        {reviewer.isProfileComplete ? (
                          <Badge className="bg-emerald-500 text-white hover:bg-emerald-500" data-testid="verified-badge">
                            <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Verified
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-500 text-white hover:bg-slate-500" data-testid="onboarding-badge">
                            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Onboarding
                          </Badge>
                        )}
                        {/* PRO Subscriber Badge */}
                        {reviewer.subscriptionActive && (
                          <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black hover:from-yellow-400 hover:to-amber-400" data-testid="pro-badge">
                            <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            PRO
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-bold mb-1">{reviewer.name}</h3>
                          <p className="text-sm text-gray-500">@{reviewer.username}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleFollowClick(reviewer)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          title="Follow this reviewer"
                        >
                          <Heart className={`h-4 w-4 ${followingStatus[reviewer.id] ? 'fill-current' : ''}`} />
                        </Button>
                      </div>

                      <p className="text-gray-600 mb-4 line-clamp-2">{reviewer.bio || 'No bio yet'}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Followers</span>
                          <span className="font-semibold text-gray-700">{reviewer.followersCount || reviewer.followers || '0'}</span>
                        </div>
                        {reviewer.isLive && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Queue</span>
                            <span className="font-semibold text-gray-700">{reviewer.queueCount} tracks</span>
                          </div>
                        )}
                        {/* MONETIZATION GATING: Only show Cash App for paid plan reviewers */}
                        {reviewer.canShowCashApp && reviewer.cash_app && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Cash App</span>
                            <a
                              href={formatCashAppUrl(reviewer.cash_app)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-semibold text-[#F2D857] hover:underline"
                            >
                              {reviewer.cash_app}
                            </a>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={() => navigate(`/reviewer/${reviewer.username}`)}
                          variant="outline"
                          className="flex-1 border-2 border-black hover:bg-black hover:text-white transition-all"
                        >
                          View Profile
                        </Button>
                        
                        {(reviewer.is_live || reviewer.isLive) ? (
                          <Button
                            onClick={() => handleJoinLiveSession(reviewer)}
                            className="flex-1 bg-black hover:bg-black/90 text-white font-bold transition-all"
                          >
                            <Radio className="h-4 w-4 mr-2" />
                            Join Live Session
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleNotifyMe(reviewer)}
                            disabled={notifyingReviewer === reviewer.id}
                            className="flex-1 bg-gray-300 text-gray-600 hover:bg-gray-400 transition-all disabled:cursor-not-allowed"
                          >
                            {notifyingReviewer === reviewer.id ? 'Subscribing...' : 'Notify Me'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Notify Me Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative animate-fadeIn">
            <button
              onClick={() => {
                setShowNotifyModal(false);
                setNotifyEmail('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="mb-6">
              <h2 className="text-2xl font-bold text-black mb-2">
                🔔 Get Notified
              </h2>
              <p className="text-gray-600">
                {selectedReviewer?.name} is not live right now. Want to be notified when they're available?
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <Input
                type="email"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full"
              />
            </div>

            <Button
              onClick={submitNotifyMe}
              disabled={!notifyEmail || notifyingReviewer}
              className="w-full bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold py-3"
            >
              {notifyingReviewer ? 'Subscribing...' : 'Notify Me'}
            </Button>
          </div>
        </div>
      )}

      {/* Follow Modal */}
      <FollowModal
        reviewer={selectedReviewer}
        isOpen={showFollowModal}
        onClose={() => setShowFollowModal(false)}
        onSuccess={handleFollowSuccess}
      />
    </div>
  );
};

export default Reviewers;
